const STORAGE_PREFIX = 'bloodeye_compliance';

function toLocalDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function getTodayKey() {
    return toLocalDateKey(new Date());
}

function storageKey(userId) {
    return userId ? `${STORAGE_PREFIX}_${userId}` : STORAGE_PREFIX;
}

export function loadComplianceData(userId) {
    try {
        const raw = localStorage.getItem(storageKey(userId));
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

export function saveComplianceData(userId, data) {
    localStorage.setItem(storageKey(userId), JSON.stringify(data));
}

export function clearComplianceData(userId) {
    localStorage.removeItem(storageKey(userId));
    localStorage.removeItem('bloodeye_notification_settings');
    localStorage.removeItem('bloodeye_notification_banner_dismissed');
}

export function calculateStreak(data) {
    let streak = 0;
    const today = new Date();
    const todayKey = getTodayKey();
    const todayEntry = data[todayKey];
    const todayComplete = todayEntry?.completedAt != null;

    if (todayComplete) streak = 1;

    for (let i = 1; i < 365; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = toLocalDateKey(d);
        const entry = data[key];
        if (entry?.completedAt) {
            streak++;
        } else {
            break;
        }
    }
    return streak;
}

export function calculateComplianceRate(data, days = 7) {
    const today = new Date();
    let completed = 0;
    let total = 0;

    for (let i = 0; i < days; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = toLocalDateKey(d);
        const entry = data[key];
        if (entry) {
            total++;
            if (entry.completedAt) completed++;
        }
    }

    return total === 0 ? 0 : Math.round((completed / total) * 100);
}

export function getChecklistForDate(data, dateKey, categories) {
    const items = data[dateKey]?.items || {};
    const checklist = {};
    for (const cat of categories) {
        checklist[cat] = items[cat] === true;
    }
    return checklist;
}

/**
 * Merge server compliance data with local.
 * Server wins for past days, local wins for today (may have unsaved changes).
 */
export function mergeComplianceData(local, serverDays) {
    if (!serverDays || typeof serverDays !== 'object') return local;
    const todayKey = getTodayKey();
    const merged = { ...serverDays };
    // Local today overrides server (user may have checked items since last sync)
    if (local[todayKey]) {
        merged[todayKey] = local[todayKey];
    }
    return merged;
}

// --- Weekly tracking helpers ---

function nextDay(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    return toLocalDateKey(d);
}

export function getWeekRange(dateKey) {
    const d = new Date(dateKey + 'T00:00:00');
    const dow = d.getDay(); // 0=Sun, 1=Mon, ...
    const diffToMon = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(d);
    mon.setDate(mon.getDate() + diffToMon);
    const sun = new Date(mon);
    sun.setDate(sun.getDate() + 6);
    return { start: toLocalDateKey(mon), end: toLocalDateKey(sun) };
}

export function getWeeklyChecklistForDate(data, dateKey, weeklyItemKeys) {
    const weeklyItems = data[dateKey]?.weeklyItems || {};
    const checklist = {};
    for (const key of weeklyItemKeys) {
        checklist[key] = weeklyItems[key] === true;
    }
    return checklist;
}

export function getWeeklyProgress(data, dateKey, weeklyItemKeys, weeklyTargets) {
    const { start, end } = getWeekRange(dateKey);
    const progress = {};
    for (const key of weeklyItemKeys) {
        let count = 0;
        for (let d = start; d <= end; d = nextDay(d)) {
            if (data[d]?.weeklyItems?.[key]) count++;
        }
        progress[key] = { done: count, target: weeklyTargets[key] || 1 };
    }
    return progress;
}

export function getHistoryDays(data, count = 7) {
    const today = new Date();
    const days = [];

    for (let i = count - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = toLocalDateKey(d);
        const entry = data[key];
        const isToday = i === 0;

        let status = 'none'; // no data
        if (entry?.completedAt) {
            status = 'complete';
        } else if (entry && Object.values(entry.items || {}).some(Boolean)) {
            status = 'partial';
        } else if (!isToday && entry) {
            status = 'missed';
        }

        days.push({
            date: key,
            label: d.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0),
            status,
            isToday,
        });
    }

    return days;
}
